import Fuse from 'fuse.js'
import { useCallback, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'

const useFuse = (
  list: any[],
  options?: Fuse.IFuseOptions<any>,
  limit?: number,
) => {
  // defining our query state in there directly
  const [query, setQuery] = useState('')

  // let's memoize the fuse instance for performances
  const fuse = useMemo(() => {
    return new Fuse(list, options)
  }, [list, options])

  // memoize results whenever the query or options change
  const hits = useMemo(
    // if query is empty and `matchAllOnEmptyQuery` is `true` then return all list
    // NOTE: we remap the results to match the return structure of `fuse.search()`
    () => fuse.search(query.trim(), limit ? { limit } : undefined),
    [fuse, limit, query],
  )

  // debounce setQuery and rename it `debouncedSetQuery` so it's transparent
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetQuery = useCallback(debounce(setQuery, 1000), [])

  // pass handling helpers to speed up implementation
  const onChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      debouncedSetQuery(e.target.value),
    [debouncedSetQuery],
  )

  const onSubmitSearch = useCallback(
    (query: string) => debouncedSetQuery(query),
    [debouncedSetQuery],
  )

  // still returning `setQuery` for custom handler implementations
  return {
    hits,
    query,
    debouncedSetQuery,
    onChangeSearch,
    onSubmitSearch,
    setQuery,
  }
}

export default useFuse
